import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, ChevronDown, ChevronUp, Sparkles } from "lucide-react";
import { aboutContentSchema, type AboutContent } from "@profolio/validators";

interface AboutEditorProps {
  content: Record<string, unknown>;
  onChange: (content: AboutContent) => void;
}

export function AboutEditor({ content, onChange }: AboutEditorProps) {
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);

  const parseResult = aboutContentSchema.safeParse(content);
  const data: AboutContent = parseResult.success
    ? parseResult.data
    : { heading: "About Me", body: "", highlights: [] };

  const { heading = "About Me", body = "", highlights = [] } = data;

  const notifyChange = (
    newHeading: string,
    newBody: string,
    newHighlights: Array<{ label: string; value: string }>
  ) => {
    onChange({
      heading: newHeading,
      body: newBody,
      highlights: newHighlights,
    });
  };

  const handleHeadingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    notifyChange(e.target.value, body, highlights);
  };

  const handleBodyChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    notifyChange(heading, e.target.value, highlights);
  };

  const addHighlight = () => {
    if (highlights.length >= 10) return;
    const updated = [...highlights, { label: "", value: "" }];
    setFocusedIndex(highlights.length);
    setDetailsOpen(true);
    notifyChange(heading, body, updated);
  };

  const updateHighlight = (index: number, field: "label" | "value", value: string) => {
    const updated = [...highlights];
    updated[index] = { ...updated[index], [field]: value };
    notifyChange(heading, body, updated);
  };

  const removeHighlight = (index: number) => {
    const updated = highlights.filter((_, i) => i !== index);
    notifyChange(heading, body, updated);
  };

  return (
    <div className="space-y-6">
      {/* ── 1. Essential Fields ── */}
      <div>
        <Label htmlFor="about-heading" className="text-xs font-semibold">
          Section Heading
        </Label>
        <Input
          id="about-heading"
          value={heading}
          onChange={handleHeadingChange}
          placeholder="About Me"
          className="mt-1 text-xs"
        />
      </div>

      <div>
        <Label htmlFor="about-body" className="text-xs font-semibold">
          Academic Biography / Profile Summary
        </Label>
        <p className="text-[11px] text-muted-foreground mb-1.5">
          Write a concise narrative describing your background, research interests, and academic appointments.
        </p>
        <Textarea
          id="about-body"
          value={body}
          onChange={handleBodyChange}
          placeholder="I am an Associate Professor in..."
          rows={6}
          className="text-xs"
        />
      </div>

      {/* ── 2. Progressive Disclosure: Additional Details & Metrics ── */}
      <div className="rounded-lg border bg-card/60 overflow-hidden">
        <button
          type="button"
          onClick={() => setDetailsOpen(!detailsOpen)}
          className="flex w-full items-center justify-between p-3 text-xs font-semibold hover:bg-muted/40 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-primary" />
            <span>Highlight Metrics & Key Stats ({highlights.length})</span>
          </div>
          {detailsOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>

        {detailsOpen && (
          <div className="p-4 pt-2 border-t space-y-3">
            <p className="text-[11px] text-muted-foreground">
              Optional metric cards (e.g., "Department: Computer Science", "Lab: AI & Robotics", "H-Index: 24").
            </p>

            {highlights.length === 0 ? (
              <div className="rounded-md border border-dashed p-4 text-center text-xs text-muted-foreground">
                No highlight metrics added yet.
              </div>
            ) : (
              <div className="space-y-2">
                {highlights.map((h, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <Input
                      placeholder="Label (e.g. Department)"
                      value={h.label}
                      autoFocus={focusedIndex === index}
                      onFocus={() => setFocusedIndex(null)}
                      onChange={(e) => updateHighlight(index, "label", e.target.value)}
                      className="text-xs flex-1"
                    />
                    <Input
                      placeholder="Value (e.g. Computer Science)"
                      value={h.value}
                      onChange={(e) => updateHighlight(index, "value", e.target.value)}
                      className="text-xs flex-1"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeHighlight(index)}
                      className="text-destructive hover:bg-destructive/10 h-8 w-8 shrink-0"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addHighlight}
              disabled={highlights.length >= 10}
              className="gap-1.5 text-xs h-7"
            >
              <Plus className="h-3.5 w-3.5" /> Add Highlight Metric
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
